import { Predicate, MedicationItem, AgePredicate, WeightPredicate, PredicateEvalResult } from '../Cpr/types';


function isAgePredicate(predicate: Predicate){
    return predicate.type == 'age';
}

function formatAgeUnit(unit: string, t: any): string {
  if (unit == "years") {
    return t('age.years');
  } else if (unit == "months") {
    return t('age.months');
  }
  else {
    return t('age.days');
  }
}

function formatAgeWithUnit(age: number, unit: string, t: any): { value: string; unit: string } {
  // Convert to days for easier comparison
  let ageInDays: number;
  if (unit === 'years') {
    ageInDays = age * 365;
  } else if (unit === 'months') {
    ageInDays = age * 30;
  } else {
    ageInDays = age;
  }

  // Special case: approximately 1 week (5-9 days)
  if (ageInDays >= 5 && ageInDays <= 9) {
    return { value: t('age.oneWeek'), unit: '' };
  }

  // Special case: newborn (0-1 days)
  if (ageInDays >= 0 && ageInDays <= 1) {
    return { value: t('age.newborn'), unit: '' };
  }

  // For small ages in months (less than 1 month), convert to days
  if (unit === 'months' && age < 1) {
    const days = Math.round(age * 30);
    if (days <= 1) {
      return { value: t('age.newborn'), unit: '' };
    }
    return { value: String(days), unit: t('age.days') };
  }

  return { value: String(age), unit: formatAgeUnit(unit, t) };
}

export function evaluateAgePredicate(predicate: AgePredicate, patientAgeInMonths: number, t: any): PredicateEvalResult {
  if (!isAgePredicate(predicate)) {
    return { isValid: false };
  }

  const predicateMinAgeInMonths = predicate.unit === 'years' ? (predicate.minAge || 0) * 12 : predicate.minAge;
  const predicateMaxAgeInMonths = predicate.unit === 'years' ? (predicate.maxAge || 0) * 12 : predicate.maxAge;

  if (predicateMinAgeInMonths && !predicateMaxAgeInMonths && patientAgeInMonths < predicateMinAgeInMonths) {
    const formatted = formatAgeWithUnit(predicate.minAge!, predicate.unit, t);
    return {
      isValid: false,
      message: t('predicates.ageMin', { minAge: formatted.value, unit: formatted.unit })
    };
  }

  if (predicateMaxAgeInMonths && !predicateMinAgeInMonths && patientAgeInMonths > predicateMaxAgeInMonths) {
    const formatted = formatAgeWithUnit(predicate.maxAge!, predicate.unit, t);
    return {
      isValid: false,
      message: t('predicates.ageMax', { maxAge: formatted.value, unit: formatted.unit })
    };
  }

  if (predicateMinAgeInMonths && predicateMaxAgeInMonths) {
    if (patientAgeInMonths < predicateMinAgeInMonths || patientAgeInMonths > predicateMaxAgeInMonths) {
      const formattedMin = formatAgeWithUnit(predicate.minAge!, predicate.unit, t);
      const formattedMax = formatAgeWithUnit(predicate.maxAge!, predicate.unit, t);
      return {
        isValid: false,
        message: t('predicates.ageRange', { minAge: formattedMin.value, maxAge: formattedMax.value, unit: formattedMax.unit })
      };
    }
  }

  return { isValid: true };
}

export function evaluateWeightPredicate(predicate: WeightPredicate, weight: number, t: any): PredicateEvalResult {
  if (!weight || predicate.type !== 'weight') {
    return { isValid: false };
  }

  const minWeight = predicate.minWeight;
  const maxWeight = predicate.maxWeight;

  if (minWeight && !maxWeight && weight < minWeight) {
    return {
      isValid: false,
      message: t('predicates.weightMin', { minWeight })
    };
  }

  if (!minWeight && maxWeight && weight > maxWeight) {
    return {
      isValid: false,
      message: t('predicates.weightMax', { maxWeight })
    };
  }

  if (minWeight && maxWeight) {
    if (weight < minWeight || weight > maxWeight) {
      return {
        isValid: false,
        message: t('predicates.weightRange', { minWeight, maxWeight })
      };
    }
  }

  return { isValid: true };
}

export function evaluateMedicationPredicate(
  medication: MedicationItem,
  age: number,
  weight: number,
  t: any
): PredicateEvalResult | null {
  // If no predicates, medication is always valid
  if (!medication.cases || medication.cases.length === 0) {
    return null;
  }

  // Try to find a matching case
  for (const case_ of medication.cases) {
    const predicate = case_.predicate;
    const result = predicate.type === 'age' 
      ? evaluateAgePredicate(predicate as AgePredicate, age, t)
      : evaluateWeightPredicate(predicate as WeightPredicate, weight, t);

    if (result.isValid) {
      return { isValid: true };
    }
  }

  // If no case matched, return the message from first predicate
  // (assuming we don't have mixed predicates for the same medication)
  const firstCase = medication.cases[0];
  return firstCase.predicate.type === 'age'
    ? evaluateAgePredicate(firstCase.predicate as AgePredicate, age, t)
    : evaluateWeightPredicate(firstCase.predicate as WeightPredicate, weight, t);
}

export function getMedicationDetails(medication: MedicationItem, age: number, weight: number, t: any) {
  if (!medication.cases || medication.cases.length === 0) {
    return medication;
  }

  // Find first matching case
  const matchingCase = medication.cases.find(case_ => {
    const predicate = case_.predicate;
    const result = predicate.type === 'age'
      ? evaluateAgePredicate(predicate as AgePredicate, age, t)
      : evaluateWeightPredicate(predicate as WeightPredicate, weight, t);
    return result.isValid;
  });

  if (matchingCase) {
    return {
      ...medication,
      ...matchingCase.details
    };
  }

  // Return base medication without details if no case matches
  return medication;
}